/**
 * Legal API — acceptation des documents légaux (CGU, Politique, CGV).
 *
 * @remarks
 * Back livré dans `gateway/src/legal/` (cf. KB SAAS-A-131). Convention front :
 * toute réponse consommée est validée par un schema Zod du contrat partagé
 * (`@intra-alt/shared-types/dtos`) — jamais de cast direct.
 *
 * - `GET /api/legal/pending` → docs dont la version courante ≠ version acceptée
 * - `POST /api/legal/accept` → enregistre une (re-)acceptation (IP/UA captés
 *   côté serveur ; le client ne déclare que `{ type, version }`)
 */

import { z } from 'zod';
import { LegalDocumentType } from '@intra-alt/shared-types';
import { apiClient } from '@/lib/http/client';
import type { ApiResponse } from '@/lib/http/types';

/**
 * Schema local (front-Zod) construit autour de l'enum partagé `LegalDocumentType`
 * — source de vérité du contrat. On ne réutilise pas `pendingLegalDocumentSchema`
 * de la lib pour la validation : l'inférence Zod cross-package élargit `type` en
 * `any` côté front (Zod 4 ≠ zod bundlé lib). Mêmes champs, même contrat.
 */
const PendingLegalDocumentSchema = z.object({
  type: z.enum(LegalDocumentType),
  version: z.string(),
});

export type PendingLegalDocument = z.infer<typeof PendingLegalDocumentSchema>;

const PendingLegalArraySchema = z.array(PendingLegalDocumentSchema);

/** GET /api/legal/pending — documents en attente d'acceptation par l'user courant. */
export async function fetchPendingLegal(): Promise<PendingLegalDocument[]> {
  const response = await apiClient.get<ApiResponse<unknown>>('/api/legal/pending');
  return PendingLegalArraySchema.parse(response.data.datas);
}

/** POST /api/legal/accept — enregistre l'acceptation d'une version de document. */
export async function acceptLegal(body: PendingLegalDocument): Promise<undefined> {
  await apiClient.post<ApiResponse<unknown>>('/api/legal/accept', body);
  return undefined;
}
